/**
 * Funding Health — single-number verdict blending the four KPI signals
 * that live on the Funding Hub tile so founders get a one-glance read
 * of their overall funding picture before drilling into any widget:
 *
 *   1. Runway        (calcRunway.status)
 *   2. Capital stack (personal vs. secured non-personal capital)
 *   3. Net vs plan   (performance_summary.net.favorable)
 *   4. Marketing     (Aug 2026 · computeMarketingHealth().pct — pipeline
 *                     drives near-term revenue which pulls the runway
 *                     equation. Replaces the retired Grants leg.)
 *
 * Aug 2026 · Grants leg REMOVED, Marketing leg ADDED. Awarded grants
 * already show up in the Capital Stack leg (they add to `non-personal
 * secured capital`), so a dedicated Grants leg was double-counting the
 * same signal. Marketing was promoted in its place because marketing
 * pipeline is the leading indicator of revenue that keeps the runway
 * equation solvent.
 *
 * Each leg scores 0–100 and the four are averaged (fixed denominator
 * of 4) so missing signals honestly drag the number down — mirrors the
 * "Launch readiness" (plan+roadmap+compliance)/3 pattern on the Hub.
 *
 * Colour band uses the same ok/watch/critical ladder as RunwayCard so
 * the surface reads consistently:
 *   - ≥ 75  → green  "Strong"
 *   - 50–75 → amber  "Building"
 *   - < 50  → red    "Fragile"
 *   - no plan → grey "—"
 */
import { calcRunway } from "./runway";
import { computeMarketingHealth } from "./marketingHealth";

export const FUNDING_HEALTH_COLOR = {
  strong: "#059669",
  building: "#F59E0B",
  fragile: "#DC2626",
  unknown: "#9CA3AF",
};

export const FUNDING_HEALTH_LABEL = {
  strong: "Strong",
  building: "Building",
  fragile: "Fragile",
  unknown: "—",
};

function runwayScore(runway) {
  switch (runway?.status) {
    case "infinite":
    case "ok":
      return 100;
    case "watch":
      return 60;
    case "critical":
      return 20;
    default:
      return 0;
  }
}

function capitalStackScore(runway) {
  if (!runway || runway.status === "unknown") return 0;
  const nonPersonal =
    (runway.funded_investor_capital || 0) +
    (runway.awarded_grant_capital || 0) +
    (runway.crm_won_capital || 0) +
    (runway.funded_loan_capital || 0);
  const base = runway.base_savings || 0;
  if (nonPersonal <= 0) return 20;
  if (base <= 0) return 100;
  if (nonPersonal >= base) return 100;
  return 60;
}

function netVsPlanScore(business) {
  const net = business?.performance_summary?.net;
  if (!net) return 0;
  return net.favorable ? 100 : 30;
}

function marketingScore(business) {
  const mh = computeMarketingHealth(business);
  if (!mh || mh.status === "unknown" || mh.legs == null) return 0;
  return mh.pct;
}

/**
 * Compute the Funding Health verdict for a business.
 * Returns `{ status: "unknown" }` when there is no plan yet so the
 * Funding tile can render a neutral affordance (matches RunwayCard).
 */
export function computeFundingHealth(business) {
  if (!business?.plan) return { pct: 0, status: "unknown", legs: null };
  const runway = calcRunway(business);
  const legs = {
    runway: runwayScore(runway),
    capital_stack: capitalStackScore(runway),
    net_vs_plan: netVsPlanScore(business),
    marketing: marketingScore(business),
  };
  const pct = Math.round(
    (legs.runway + legs.capital_stack + legs.net_vs_plan + legs.marketing) / 4,
  );
  let status;
  if (pct >= 75) status = "strong";
  else if (pct >= 50) status = "building";
  else status = "fragile";
  return { pct, status, legs };
}

// Aug 2026 · Funding Health blends four legs at equal weight (25% each).
// Exported so the strip can render the same weight-per-leg pattern the
// Operating Health hero uses. (Grants dropped, Marketing added — see
// the file header comment for the full rationale.)
export const FUNDING_HEALTH_WEIGHTS = {
  runway: 25,
  capital_stack: 25,
  net_vs_plan: 25,
  marketing: 25,
};

function nextLeverForCapital(score, id) {
  if (score >= 100) return null;
  const next = score === 20 ? 60 : 100;
  const action =
    score === 20
      ? "Log an investor commit, awarded grant or funded loan"
      : "Grow secured capital until it matches your savings";
  return { action, to: `/business/${id}/plan#section-funding`, delta: (next - score) / 4 };
}

function nextLeverForRunway(score, id) {
  if (score >= 100) return null;
  const next = score === 20 ? 60 : 100;
  const action =
    score === 20
      ? "Cut monthly burn or add revenue to lift runway"
      : "Push runway past 6 months to lock in Solid";
  return { action, to: `/business/${id}/plan#section-budget`, delta: (next - score) / 4 };
}

function nextLeverForNetVsPlan(score, id) {
  if (score >= 100) return null;
  const next = score === 0 ? 30 : 100;
  const action =
    score === 0
      ? "Log this month's actuals to unlock Net-vs-plan"
      : "Close the gap so this month flips favorable";
  return { action, to: `/business/${id}/performance`, delta: (next - score) / 4 };
}

function nextLeverForMarketing(score, id) {
  if (score >= 100) return null;
  // Marketing composite bands roughly 0/30/60/100 like the leg scoring —
  // use "grow the score by one rung" advice.
  const next = score < 30 ? 30 : score < 60 ? 60 : 100;
  const action =
    score < 30
      ? "Log audience, reach and pipeline data on the Marketing tile"
      : score < 60
        ? "Push weighted pipeline past $1k to lift Growth Health"
        : "Push weighted pipeline past $10k to lock in Strong";
  return { action, to: `/business/${id}/campaigns`, delta: (next - score) / 4 };
}

// Fixed priority when multiple legs share the lowest score — cheapest
// action first (Net-vs-plan → Marketing → Runway → Capital stack) so
// the hint always surfaces the move a founder can take today.
const LEG_ORDER = ["net_vs_plan", "marketing", "runway", "capital_stack"];
const LEG_LABELS = {
  runway: "Runway",
  capital_stack: "Capital stack",
  net_vs_plan: "Net vs plan",
  marketing: "Marketing",
};

/**
 * Given a Funding Health result, surface the single weakest leg and the
 * concrete next move that would lift it to the next rung — plus the +X%
 * uplift on the overall Funding Health score if that rung is reached.
 *
 * Returns `null` when there is no plan or every leg is already maxed.
 */
export function nextFundingLever(business) {
  if (!business?.plan) return null;
  const { legs } = computeFundingHealth(business);
  if (!legs) return null;
  const minScore = Math.min(...LEG_ORDER.map((k) => legs[k]));
  if (minScore >= 100) return null;
  const key = LEG_ORDER.find((k) => legs[k] === minScore);
  const id = business.id;
  let lever;
  if (key === "net_vs_plan") lever = nextLeverForNetVsPlan(legs.net_vs_plan, id);
  else if (key === "runway") lever = nextLeverForRunway(legs.runway, id);
  else if (key === "marketing") lever = nextLeverForMarketing(legs.marketing, id);
  else lever = nextLeverForCapital(legs.capital_stack, id);
  if (!lever) return null;
  return {
    leg: key,
    leg_label: LEG_LABELS[key],
    action: lever.action,
    to: lever.to,
    delta_pct: Math.round(lever.delta),
  };
}
